import React, { useEffect, useState, useRef } from "react";
import { useReactToPrint } from "react-to-print";
import UniversalInvoicePrint from "./UniversalInvoicePrint";
import api from "../Service/api";
import "./Purchases.css";

function ViewFactoryUse() {
  const getLocalDate = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, "0");
    const day = String(now.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const formatToDateOnly = (datetime) => {
    if (!datetime) return "-";
    if (typeof datetime === "string" && datetime.match(/^\d{4}-\d{2}-\d{2}$/)) {
      return datetime;
    }
    try {
      const date = new Date(datetime);
      if (isNaN(date.getTime())) return "-";
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    } catch (e) {
      return "-";
    }
  };

  // Helper to sort invoices descending by invoice number
const sortInvoicesDesc = (arr) => {
  return arr.sort((a, b) => (parseInt(b.invoiceNumber) || 0) - (parseInt(a.invoiceNumber) || 0));
};

  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [allProducts, setAllProducts] = useState([]);
  const [categoryList, setCategoryList] = useState([]);
  const [vendorList, setVendorList] = useState([]);
  const [customerList, setCustomerList] = useState([]);
  const [startDate, setStartDate] = useState(getLocalDate());
  const [endDate, setEndDate] = useState(getLocalDate());

  const [loadingProducts, setLoadingProducts] = useState(true);
  const [loadingCategories, setLoadingCategories] = useState(true);
  const [loadingVendors, setLoadingVendors] = useState(true);
  const [loadingCustomers, setLoadingCustomers] = useState(true);

  const printRef = useRef();
  const [printInvoice, setPrintInvoice] = useState(null);
  const [loggedInUser, setLoggedInUser] = useState(null);
  const [filteredInvoices, setFilteredInvoices] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [fullScreenImage, setFullScreenImage] = useState(null);

  // ---- UPDATE STATE ----
  const [updateInvoice, setUpdateInvoice] = useState(null);
  const [selectedCustomerBalance, setSelectedCustomerBalance] = useState(null);
  const [isUpdating, setIsUpdating] = useState(false);

  // ---- STOCK CACHE FOR SELECTED PRODUCTS ----
  const [productStockMap, setProductStockMap] = useState({});

  const isInitialLoading = loadingProducts || loadingCategories || loadingVendors || loadingCustomers;

  // ---- Fetch vendors and customers ----
  useEffect(() => {
    setLoadingVendors(true);
    api
      .get("/product/getAll")
      .then((res) => {
        if (res.data.code === 1 && Array.isArray(res.data.additionalDetail)) {
          setVendorList(res.data.additionalDetail);
        }
      })
      .catch((err) => console.error("Failed to fetch vendors", err))
      .finally(() => setLoadingVendors(false));
  }, []);

  useEffect(() => {
    setLoadingCustomers(true);
    api
      .get("/product/getAllCustomers")
      .then((res) => {
        if (res.data.code === 1 && Array.isArray(res.data.additionalDetail)) {
          setCustomerList(res.data.additionalDetail);
          console.log("✅ Customers with balance:", res.data.additionalDetail);
        }
      })
      .catch((err) => console.error("Failed to fetch customers", err))
      .finally(() => setLoadingCustomers(false));
  }, []);

  const refreshProductList = async () => {
    setLoadingProducts(true);
    try {
      const res = await api.get("/product/names-prices");
      if (res.data.code === 1) {
        setAllProducts(res.data.additionalDetail);
        const stockMap = {};
        res.data.additionalDetail.forEach((p) => {
          stockMap[p.productName] = p;
        });
        setProductStockMap(stockMap);
      }
    } catch (err) {
      console.error("Error refreshing product list", err);
    } finally {
      setLoadingProducts(false);
    }
  };

  useEffect(() => {
    const phone = localStorage.getItem("userPhone");
    if (!phone) return;
    api
      .get(`/authentication/userData/${phone}`)
      .then((res) => setLoggedInUser(res.data.additionalDetail))
      .catch((err) => console.error("USER DATA API ERROR:", err));
  }, []);

  const handlePrint = useReactToPrint({
    content: () => printRef.current,
    documentTitle: printInvoice ? `FactoryUse-${printInvoice.invoiceNumber}` : "FactoryUse",
  });

  const handlePrintInvoice = (invoice) => {
    const copy = JSON.parse(JSON.stringify(invoice));
    copy.user = loggedInUser;
    copy.products = (copy.sales || copy.products || []).map((p) => ({
      ...p,
      calculatedTotal: computeProductTotal(p),
    }));
    copy.payBill = 0;
    copy.grandTotal = computeInvoiceGrandTotal(copy);
    setPrintInvoice(copy);
    setTimeout(() => handlePrint(), 300);
  };

  useEffect(() => {
    loadFactoryUse();
    loadProductList();
    loadCategoryList();
  }, []);

  useEffect(() => {
    if (loading === false) {
      loadFactoryUse();
    }
  }, [startDate, endDate]);

  const loadFactoryUse = async () => {
    try {
      const params = {};
      if (startDate) params.startDate = startDate;
      if (endDate) params.endDate = endDate;
      const res = await api.get("/factory/factory-use-records", { params });
      const data = res.data.code === 1 ? res.data.additionalDetail || [] : [];
const sortedData = sortInvoicesDesc(data);   // ← add this line
setInvoices(sortedData);
setFilteredInvoices(sortedData);
    } catch (err) {
      console.error("Error fetching factory use records", err);
      setInvoices([]);
      setFilteredInvoices([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const handleWheel = (e) => {
      if (e.target.type === 'number') {
        e.preventDefault();
      }
    };
    document.addEventListener('wheel', handleWheel, { passive: false });
    return () => document.removeEventListener('wheel', handleWheel);
  }, []);

  const resetFilters = () => {
    setStartDate("");
    setEndDate("");
    setSearchText("");
    loadFactoryUse();
  };

  const handleSearch = (text) => {
    setSearchText(text);
    if (!text) {
      setFilteredInvoices(invoices);
      return;
    }
    const filtered = invoices.filter((inv) => {
  const customerMatch = inv.customerName?.toLowerCase().includes(text.toLowerCase());
  const productMatch = (inv.products || []).some((p) =>
    p.productName?.toLowerCase().includes(text.toLowerCase())
  );
  const invoiceMatch = inv.invoiceNumber?.toString().includes(text);
  return customerMatch || productMatch || invoiceMatch;
});
setFilteredInvoices(sortInvoicesDesc(filtered));   // ← sort here
  };

  const loadProductList = async () => {
    setLoadingProducts(true);
    try {
      const res = await api.get("/product/names-prices");
      if (res.data.code === 1) {
        setAllProducts(res.data.additionalDetail);
        const stockMap = {};
        res.data.additionalDetail.forEach((p) => {
          stockMap[p.productName] = p;
        });
        setProductStockMap(stockMap);
        console.log("✅ Products with stock:", res.data.additionalDetail);
      }
    } catch (err) {
      console.error("Error loading product list", err);
      setAllProducts([]);
    } finally {
      setLoadingProducts(false);
    }
  };

  const loadCategoryList = async () => {
    setLoadingCategories(true);
    try {
      const res = await api.get("/api/category/getCategories");
      setCategoryList(res.data.additionalDetail || []);
    } catch (err) {
      setCategoryList([
        { categoryName: "packet" },
        { categoryName: "roll" },
        { categoryName: "reel" },
        { categoryName: "tota" },
        { categoryName: "box" },
        { categoryName: "kg" },
        { categoryName: "bottle" },
      ]);
    } finally {
      setLoadingCategories(false);
    }
  };

  const computeProductTotal = (prod) => {
    const category = (prod.category || "").toLowerCase();
    const price = parseFloat(prod.price) || 0;
    const quantity = parseFloat(prod.quantity) || 0;
    const size = parseFloat(prod.size) || 1;
    const ktae = parseFloat(prod.ktae) || 1;
    const gram = parseFloat(prod.gram) || 1;
    let total = 0;
    if (category === "packet") total = (quantity * size * ktae * gram * price) / 15500;
    else if (category === "roll") total = size * quantity * price;
    else if (category === "reel" || category === "tota") total = quantity * price;
    else if (category === "box") total = price * quantity;
    else total = price * quantity;
    return Number(total.toFixed(2));
  };

  const computeInvoiceGrandTotal = (invoice) => {
    if (!invoice) return 0;
    const productsTotal = (invoice.sales || invoice.products || []).reduce(
      (total, p) => total + computeProductTotal(p),
      0
    );
    return Number(productsTotal.toFixed(2));
  };

  // ---- DELETE ----
  const deleteInvoice = async (invoiceNum) => {
    if (!window.confirm(`Delete factory use invoice ${invoiceNum}?`)) return;
    try {
      await api.delete(`/factory/factory-use/delete/${invoiceNum}`);
      alert("Deleted!");
      loadFactoryUse();
    } catch (err) {
      console.error("Delete failed", err);
      alert("Delete failed");
    }
  };

  // ---- UPDATE ----
  const openUpdateForm = (invoice) => {
    // 🛡️ Guard against canceled invoices
    if (invoice.isActive === false) {
      alert("This invoice is canceled and cannot be edited.");
      return;
    }

    const copy = JSON.parse(JSON.stringify(invoice || {}));
    copy.products = (copy.products || []).map((p) => ({
      ...p,
      quantity: p.quantity ?? 0,
      price: p.price ?? 0,
      size: p.size ?? "",
      length: p.length ?? "",
      ktae: p.ktae ?? "",
      gram: p.gram ?? "",
      boxSize: p.boxSize ?? "",
      particular: p.particular ?? "",
      calculatedTotal: computeProductTotal(p),
    }));
    copy.customerName = invoice.customerName ?? "";
    copy.description = invoice.description ?? "";
    copy.invoiceDate = invoice.invoiceDate || getLocalDate();
    copy.grandTotal = computeInvoiceGrandTotal(copy);
    setSelectedCustomerBalance(null);
    setUpdateInvoice(copy);
  };

  const handleUpdateCustomerChange = (e) => {
    const name = e.target.value;
    setUpdateInvoice((prev) => ({ ...prev, customerName: name }));
    const customer = customerList.find((c) => c.customerName === name);
    if (customer && customer.currentBalance !== undefined) {
      setSelectedCustomerBalance(customer.currentBalance);
    } else {
      setSelectedCustomerBalance(null);
    }
  };

  const handleUpdateProductChange = (index, field, value) => {
    if (!updateInvoice) return;
    const products = [...updateInvoice.products];
    const prod = { ...products[index] };
    prod[field] = value;

    if (field === "productName") {
      const match = allProducts.find(
        (ap) => (ap.productName || "").toLowerCase() === value.toLowerCase()
      );
      if (match) {
        prod.price = match.sellPrice ?? prod.price;
        prod.category = match.category ?? prod.category;
        if (match.category && match.category.toLowerCase() === "roll" && match.size) {
          prod.size = match.size;
        }
        if (!productStockMap[value]) {
          setProductStockMap((prev) => ({ ...prev, [value]: match }));
        }
      }
    }

    const currentCategory = (prod.category || "").toLowerCase();
    if (
      field === "quantity" &&
      (currentCategory === "reel" || currentCategory === "tota" || currentCategory === "roll")
    ) {
      const weightValue = parseFloat(value) || 0;
      prod.size = weightValue;
    }

    if (field === "category") {
      const cat = (value || "").toLowerCase();
      if (cat !== "box") prod.boxSize = "";
      if (cat !== "packet") {
        prod.ktae = "";
        prod.gram = "";
      }
      if (!["packet", "roll", "reel", "tota", "box"].includes(cat)) prod.size = "";
      if (cat !== "reel" && cat !== "tota") prod.length = "";
    }

    prod.calculatedTotal = computeProductTotal(prod);
    products[index] = prod;
    const updatedInvoice = { ...updateInvoice, products };
    updatedInvoice.grandTotal = computeInvoiceGrandTotal(updatedInvoice);
    setUpdateInvoice(updatedInvoice);
  };

  const handleUpdateInvoiceField = (field, value) => {
    if (!updateInvoice) return;
    const updated = { ...updateInvoice, [field]: value };
    updated.grandTotal = computeInvoiceGrandTotal(updated);
    setUpdateInvoice(updated);
  };

  const saveUpdate = async () => {
    if (!updateInvoice) return alert("No invoice to update");
    setIsUpdating(true);
    try {
      const toSend = JSON.parse(JSON.stringify(updateInvoice));
      toSend.products = (toSend.products || []).map((p) => {
        const cat = (p.category || "").toLowerCase();
        const clean = { ...p };
        if (cat === "box") {
          clean.boxSize = p.boxSize || "";
          clean.particular = p.particular || "";
          clean.size = 0;
          clean.ktae = 0;
          clean.gram = 0;
        } else if (cat === "packet") {
          clean.size = parseFloat(p.size) || 0;
          clean.ktae = parseFloat(p.ktae) || 0;
          clean.gram = parseFloat(p.gram) || 0;
          clean.boxSize = null;
          clean.particular = p.particular || "";
        } else if (cat === "roll") {
          clean.size = parseFloat(p.size) || 0;
          clean.ktae = 0;
          clean.gram = 0;
          clean.boxSize = null;
          clean.particular = p.particular || "";
        } else if (cat === "reel" || cat === "tota") {
          clean.size = parseFloat(p.quantity) || 0;
          clean.length = parseFloat(p.length) || 0;
          clean.ktae = 0;
          clean.gram = 0;
          clean.boxSize = null;
          clean.particular = p.particular || "";
        } else {
          clean.size = 0;
          clean.ktae = 0;
          clean.gram = 0;
          clean.boxSize = null;
          clean.particular = p.particular || "";
        }
        clean.calculatedTotal = computeProductTotal(clean);
        return clean;
      });

      const invoiceNum = toSend.invoiceNumber;
      await api.put(`/factory/factory-use/update/${invoiceNum}`, {
        products: toSend.products,
        description: toSend.description,
        customerName: toSend.customerName,
        invoiceDate: toSend.invoiceDate,
      });
      alert("Factory use updated successfully!");
      setUpdateInvoice(null);
      setSelectedCustomerBalance(null);
      await refreshProductList();
      loadFactoryUse();
    } catch (err) {
      console.error("Update failed", err);
      alert("Update failed! See console.");
    } finally {
      setIsUpdating(false);
    }
  };

  const showWeightLabel = updateInvoice?.products?.some((p) => {
    const cat = (p.category || "").toLowerCase();
    return cat === "reel" || cat === "tota" || cat === "roll";
  });

  const renderProductStock = (productName) => {
    const stockInfo = productStockMap[productName];
    if (!stockInfo) return null;

    const cat = (stockInfo.category || "").toLowerCase();

    if ((cat === "reel" || cat === "tota" || cat === "roll") && stockInfo.stockByWeight) {
      const entries = Object.entries(stockInfo.stockByWeight).filter(([_, qty]) => qty > 0);
      if (entries.length === 0)
        return (
          <div style={{ color: "#ff4444", fontSize: "10px", marginTop: "2px" }}>
            ⚠️ Out of Stock
          </div>
        );
      return (
        <div style={{ fontSize: "10px", color: "#0a8938", marginTop: "2px" }}>
          📊 Stock:{" "}
          {entries.map(([size, qty]) => (
            <span key={size} style={{ marginRight: "6px" }}>
              {size}: {qty}
            </span>
          ))}
        </div>
      );
    }

    if (cat === "packet" && stockInfo.packetStockByVariant) {
      const entries = Object.entries(stockInfo.packetStockByVariant).filter(([_, qty]) => qty > 0);
      if (entries.length === 0)
        return (
          <div style={{ color: "#ff4444", fontSize: "10px", marginTop: "2px" }}>
            ⚠️ Out of Stock
          </div>
        );
      return (
        <div style={{ fontSize: "10px", color: "#0a8938", marginTop: "2px" }}>
          📦 Stock:{" "}
          {entries.map(([variant, qty]) => (
            <span key={variant} style={{ marginRight: "6px" }}>
              {variant}: {qty}
            </span>
          ))}
        </div>
      );
    }

    if (stockInfo.stockQuantity !== undefined) {
      const qty = parseFloat(stockInfo.stockQuantity);
      return (
        <div style={{ fontSize: "10px", color: qty > 0 ? "#0a8938" : "#ff4444", marginTop: "2px" }}>
          📦 Stock: {qty}
        </div>
      );
    }

    return null;
  };

  if (loading) return <div className="loading-text">Loading…</div>;

  return (
    <div className="purchase-container">
      <h2 className="page-title">🏭 View Factory Use</h2>

      {isInitialLoading && (
        <div
          style={{
            textAlign: "center",
            padding: "10px",
            backgroundColor: "#e3f2fd",
            marginBottom: "10px",
            borderRadius: "4px",
          }}
        >
          ⏳ Loading data... {loadingProducts && "Products "}{" "}
          {loadingCategories && "Categories "} {loadingVendors && "Vendors "}{" "}
          {loadingCustomers && "Customers "}
        </div>
      )}

      <div style={{ marginBottom: 20 }}>
        <input
          type="text"
          placeholder="Search by product, customer, or invoice number..."
          value={searchText}
          onChange={(e) => handleSearch(e.target.value)}
          className="read-only-input"
          style={{ width: 300, padding: 6 }}
          disabled={isInitialLoading}
        />
      </div>

      <div
        style={{
          marginBottom: 20,
          display: "flex",
          gap: "15px",
          alignItems: "center",
          flexWrap: "wrap",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <label style={{ fontWeight: "500", color: "#000", fontSize: "14px" }}>From:</label>
          <input
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            className="read-only-input"
            style={{ padding: "6px", borderRadius: "4px", color: "#000", backgroundColor: "#fff" }}
            disabled={isInitialLoading}
          />
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <label style={{ fontWeight: "500", color: "#000", fontSize: "14px" }}>To:</label>
          <input
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            className="read-only-input"
            style={{ padding: "6px", borderRadius: "4px", color: "#000", backgroundColor: "#fff" }}
            min={startDate}
            disabled={isInitialLoading}
          />
        </div>
        <button
          onClick={resetFilters}
          className="remove-btn"
          style={{ padding: "6px 12px", fontSize: "14px", marginLeft: "10px" }}
          disabled={isInitialLoading}
        >
          Clear Filters
        </button>
        {(startDate || endDate || searchText) && (
          <span style={{ fontSize: "14px", color: "#555", marginLeft: "10px" }}>
            Showing {filteredInvoices.length} of {invoices.length} records
            {searchText && ` for "${searchText}"`}
            {startDate && ` from ${startDate}`}
            {endDate && ` to ${endDate}`}
          </span>
        )}
      </div>

      {filteredInvoices.length === 0 && !loading && (
        <p style={{ textAlign: "center", padding: "20px", color: "#666" }}>
          {searchText || startDate || endDate
            ? "No factory use records found matching your search/filters."
            : "No factory use records found."}
        </p>
      )}

      {filteredInvoices.map((invoice) => {
        const products = invoice.products || [];
        const categories = products.map((p) => (p.category || "").toLowerCase());
        const showPacketColumns = categories.includes("packet");
        const showRollColumns = categories.some((c) => ["roll"].includes(c));
        const showBoxColumns = categories.includes("box");
        const showParticularColumn = true;
        const showLengthColumn = categories.some((c) => c === "reel" || c === "tota");
        const showSizeColumn = showPacketColumns || showRollColumns || showLengthColumn;

        // 👇 Check if invoice is canceled
        const isCanceled = invoice.isActive === false;

        return (
          <div key={invoice.invoiceNumber} className="invoice-box">
            {/* 🔴 Canceled banner */}
            {isCanceled && (
              <div
                style={{
                  backgroundColor: "#f8d7da",
                  color: "#721c24",
                  padding: "12px 16px",
                  borderRadius: "4px",
                  marginBottom: "15px",
                  border: "2px solid #f5c6cb",
                  fontWeight: "bold",
                  display: "flex",
                  alignItems: "center",
                  gap: "10px",
                }}
              >
                <span style={{ fontSize: "24px" }}>⛔</span>
                <span>THIS INVOICE HAS BEEN CANCELED / DELETED – Read‑only mode</span>
              </div>
            )}

            {/* Invoice title with Canceled badge */}
            <h3 className="invoice-title" style={{ color: "#000" }}>
              Factory Use Invoice #{invoice.invoiceNumber} – {invoice.customerName || "N/A"}
              {isCanceled && (
                <span
                  style={{
                    marginLeft: "15px",
                    backgroundColor: "#dc3545",
                    color: "white",
                    padding: "4px 12px",
                    borderRadius: "20px",
                    fontSize: "14px",
                    fontWeight: "bold",
                  }}
                >
                  CANCELLED
                </span>
              )}
            </h3>

            {invoice.invoiceImagePath && (
              <div style={{ marginBottom: "10px", cursor: "pointer" }}>
                <img
                  src={invoice.invoiceImagePath}
                  alt={`Invoice ${invoice.invoiceNumber}`}
                  style={{
                    maxWidth: "200px",
                    maxHeight: "200px",
                    border: "1px solid #ccc",
                    padding: "5px",
                    objectFit: "contain",
                  }}
                  onClick={() => setFullScreenImage(invoice.invoiceImagePath)}
                />
              </div>
            )}

            {/* Conditional buttons - hidden if canceled */}
            {!isCanceled && (
              <div style={{ marginBottom: "10px", display: "flex", gap: "10px" }}>
                <button
                  className="save-btn"
                  onClick={() => openUpdateForm(invoice)}
                  disabled={isInitialLoading}
                >
                  Update
                </button>
                <button
                  className="remove-btn"
                  onClick={() => deleteInvoice(invoice.invoiceNumber)}
                  disabled={isInitialLoading}
                >
                  Delete
                </button>
                <button
                  className="print-btn"
                  onClick={() => handlePrintInvoice(invoice)}
                  disabled={!invoice || isInitialLoading}
                >
                  Print
                </button>
              </div>
            )}

            {/* If canceled, show a message instead of buttons */}
            {isCanceled && (
              <div style={{ marginBottom: "10px", color: "#6c757d", fontStyle: "italic" }}>
                🔒 This invoice is canceled and cannot be modified.
              </div>
            )}

            <div className="purchase-table-container">
              <div className="table-scroll-wrapper">
                <table className="purchase-table">
                  <thead>
                    <tr>
                      <th>Product</th>
                      <th>Category</th>
                      <th>Quantity</th>
                      <th>Rate</th>
                      <th>Date</th>
                      {showSizeColumn && <th>Size</th>}
                      {showPacketColumns && (
                        <>
                          <th>Ktae</th>
                          <th>Gram</th>
                        </>
                      )}
                      {showBoxColumns && <th>Box Size</th>}
                      {showParticularColumn && <th>Vendor/Particular</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {products.map((p, idx) => {
                      const category = (p.category || "").toLowerCase();
                      const shouldShowParticular = category === "reel" || category === "tota";
                      const isRoll = category === "roll";
                      const isPacket = category === "packet";
                      const isReelOrTota = category === "reel" || category === "tota";

                      let sizeDisplay = "-";
                      if (isPacket || isRoll) {
                        sizeDisplay = p.size || "-";
                      } else if (isReelOrTota) {
                        sizeDisplay = p.length || "-";
                      }

                      return (
                        <tr key={idx}>
                          <td>{p.productName}</td>
                          <td>{p.category}</td>
                          <td>{p.quantity}</td>
                          <td>{p.price}</td>
                          <td>{formatToDateOnly(p.saleEntryTime)}</td>
                          {showSizeColumn && <td>{sizeDisplay}</td>}
                          {showPacketColumns && (
                            <>
                              <td>{isPacket ? p.ktae || "-" : "-"}</td>
                              <td>{isPacket ? p.gram || "-" : "-"}</td>
                            </>
                          )}
                          {showBoxColumns && (
                            <td>{category === "box" ? p.boxSize || "-" : "-"}</td>
                          )}
                          {showParticularColumn && (
                            <td>
                              {category !== "roll"
                                ? shouldShowParticular
                                  ? p.particular || "-"
                                  : p.particular || "-"
                                : "-"}
                            </td>
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="invoice-summary" style={{ color: "#000", marginTop: 10 }}>
              <p>
                <strong>Description:</strong> {invoice.description || "—"}
              </p>
              {/* Show status if canceled */}
              {isCanceled && (
                <p style={{ color: "#dc3545", fontWeight: "bold" }}>
                  <strong>Status:</strong> CANCELED
                </p>
              )}
              <p className="grand-total">
                <strong>Total Value:</strong> {invoice.grandTotal ?? "0"}
              </p>
            </div>

            {/* ---------- UPDATE FORM ---------- */}
            {updateInvoice && updateInvoice.invoiceNumber === invoice.invoiceNumber && (
              <div className="invoice-box" style={{ background: "#fff7e6", marginTop: 12 }}>
                <h3 className="invoice-title">Update Factory Use #{updateInvoice.invoiceNumber}</h3>

                <div
                  style={{
                    marginBottom: 15,
                    padding: "0 10px",
                    display: "flex",
                    gap: "20px",
                    flexWrap: "wrap",
                  }}
                >
                  <div>
                    <label style={{ fontWeight: "bold", marginRight: 10 }}>Invoice Date:</label>
                    <input
                      type="date"
                      value={updateInvoice.invoiceDate || getLocalDate()}
                      onChange={(e) => handleUpdateInvoiceField("invoiceDate", e.target.value)}
                      className="read-only-input"
                      style={{ width: "200px" }}
                      max={new Date().toISOString().split("T")[0]}
                      disabled={isUpdating}
                    />
                  </div>
                  <div>
                    <label style={{ fontWeight: "bold", marginRight: 10 }}>Customer Name:</label>
                    <input
                      type="text"
                      list="customerNamesUpdate"
                      value={updateInvoice.customerName ?? ""}
                      onChange={handleUpdateCustomerChange}
                      className="read-only-input"
                      style={{ width: "220px" }}
                      placeholder="Select Customer"
                      disabled={isUpdating}
                    />
                    <datalist id="customerNamesUpdate">
                      {customerList.map((c) => (
                        <option key={c.id} value={c.customerName}>
                          {c.customerName}{" "}
                          {c.currentBalance !== undefined
                            ? `(Balance: ${c.currentBalance})`
                            : ""}
                        </option>
                      ))}
                    </datalist>
                    {selectedCustomerBalance !== null && (
                      <span
                        style={{
                          marginLeft: "10px",
                          fontSize: "12px",
                          color: "#0a8938",
                        }}
                      >
                        Current Balance: Rs {selectedCustomerBalance.toFixed(2)}
                      </span>
                    )}
                  </div>
                </div>

                <div className="purchase-table-container">
                  <div className="table-scroll-wrapper">
                    <table className="purchase-table">
                      <thead>
                        <tr>
                          <th>Product Name</th>
                          <th>Category</th>
                          <th>{showWeightLabel ? "Weight/Quantity" : "Quantity"}</th>
                          <th>Rate</th>
                          {(() => {
                            const updateCategories = updateInvoice.products.map((p) =>
                              (p.category || "").toLowerCase()
                            );
                            const showPacket = updateCategories.includes("packet");
                            const showRoll = updateCategories.some((c) => ["roll"].includes(c));
                            const showBox = updateCategories.includes("box");
                            const showLength = updateCategories.some(
                              (c) => c === "reel" || c === "tota"
                            );
                            const showSize = showPacket || showRoll || showLength;
                            return (
                              <>
                                {showSize && <th>Size</th>}
                                {showPacket && (
                                  <>
                                    <th>Ktae</th>
                                    <th>Gram</th>
                                  </>
                                )}
                                {showBox && <th>Box Size</th>}
                                <th>Vendor/Particular</th>
                              </>
                            );
                          })()}
                        </tr>
                      </thead>
                      <tbody>
                        {updateInvoice.products.map((p, idx) => {
                          const cat = (p.category || "").toLowerCase();
                          const isReelOrTota = cat === "reel" || cat === "tota";
                          const isRoll = cat === "roll";
                          const isPacket = cat === "packet";
                          const isBox = cat === "box";
                          const selectedProduct = allProducts.find(
                            (ap) => ap.productName === p.productName
                          );

                          return (
                            <tr key={idx}>
                              <td>
                                <input
                                  list={`update-products-${idx}`}
                                  value={p.productName || ""}
                                  onChange={(e) =>
                                    handleUpdateProductChange(
                                      idx,
                                      "productName",
                                      e.target.value
                                    )
                                  }
                                  className="read-only-input"
                                  disabled={isUpdating}
                                />
                                <datalist id={`update-products-${idx}`}>
                                  {allProducts.map((ap, i) => {
                                    let label = ap.productName;
                                    if (ap.stockQuantity !== undefined)
                                      label += ` (Stock: ${ap.stockQuantity})`;
                                    return (
                                      <option key={i} value={ap.productName}>
                                        {label}
                                      </option>
                                    );
                                  })}
                                </datalist>
                                {p.productName && renderProductStock(p.productName)}
                              </td>
                              <td>
                                <input
                                  list={`update-categories-${idx}`}
                                  value={p.category || ""}
                                  onChange={(e) =>
                                    handleUpdateProductChange(idx, "category", e.target.value)
                                  }
                                  className="read-only-input"
                                  disabled={isUpdating}
                                />
                                <datalist id={`update-categories-${idx}`}>
                                  {categoryList.map((c, i) => (
                                    <option key={i} value={c.categoryName} />
                                  ))}
                                </datalist>
                              </td>
                              <td>
                                <input
                                  type="number"
                                  value={p.quantity ?? ""}
                                  onChange={(e) =>
                                    handleUpdateProductChange(idx, "quantity", e.target.value)
                                  }
                                  className="read-only-input"
                                  disabled={isUpdating}
                                  placeholder={
                                    isReelOrTota
                                      ? "Weight in kg"
                                      : isRoll
                                      ? "Quantity in meters"
                                      : "Quantity"
                                  }
                                  step={isReelOrTota ? "1" : "0.01"}
                                  onWheel={(e) => e.preventDefault()}
                                />
                              </td>
                              <td>
                                <input
                                  type="number"
                                  value={p.price ?? ""}
                                  onChange={(e) =>
                                    handleUpdateProductChange(idx, "price", e.target.value)
                                  }
                                  className="read-only-input"
                                  disabled={isUpdating}
                                  onWheel={(e) => e.preventDefault()}
                                />
                              </td>
                              {/* Size column */}
                              {(() => {
                                const updateCategories = updateInvoice.products.map((p) =>
                                  (p.category || "").toLowerCase()
                                );
                                const showSize =
                                  updateCategories.includes("packet") ||
                                  updateCategories.some((c) => ["roll"].includes(c)) ||
                                  updateCategories.some((c) => c === "reel" || c === "tota");
                                if (!showSize) return null;
                                return (
                                  <td>
                                    {isPacket || isRoll ? (
                                      <input
                                        type="number"
                                        value={p.size ?? ""}
                                        onChange={(e) =>
                                          handleUpdateProductChange(idx, "size", e.target.value)
                                        }
                                        className="read-only-input"
                                        placeholder="Size"
                                        disabled={isUpdating}
                                        onWheel={(e) => e.preventDefault()}
                                      />
                                    ) : isReelOrTota ? (
                                      <input
                                        type="number"
                                        step="any"
                                        value={p.length ?? ""}
                                        onChange={(e) =>
                                          handleUpdateProductChange(idx, "length", e.target.value)
                                        }
                                        className="read-only-input"
                                        placeholder="Length (m)"
                                        disabled={isUpdating}
                                        onWheel={(e) => e.preventDefault()}
                                      />
                                    ) : (
                                      <input
                                        type="text"
                                        value=""
                                        className="read-only-input"
                                        disabled
                                        style={{ backgroundColor: "#f0f0f0" }}
                                        placeholder="N/A"
                                      />
                                    )}
                                  </td>
                                );
                              })()}
                              {/* Packet columns */}
                              {(() => {
                                const updateCategories = updateInvoice.products.map((p) =>
                                  (p.category || "").toLowerCase()
                                );
                                if (!updateCategories.includes("packet")) return null;
                                return (
                                  <>
                                    <td>
                                      {isPacket ? (
                                        <input
                                          type="number"
                                          value={p.ktae ?? ""}
                                          onChange={(e) =>
                                            handleUpdateProductChange(
                                              idx,
                                              "ktae",
                                              e.target.value
                                            )
                                          }
                                          className="read-only-input"
                                          placeholder="Ktae"
                                          disabled={isUpdating}
                                          onWheel={(e) => e.preventDefault()}
                                        />
                                      ) : (
                                        <input
                                          type="text"
                                          value=""
                                          className="read-only-input"
                                          disabled
                                          style={{ backgroundColor: "#f0f0f0" }}
                                          placeholder="N/A"
                                        />
                                      )}
                                    </td>
                                    <td>
                                      {isPacket ? (
                                        <input
                                          type="number"
                                          value={p.gram ?? ""}
                                          onChange={(e) =>
                                            handleUpdateProductChange(
                                              idx,
                                              "gram",
                                              e.target.value
                                            )
                                          }
                                          className="read-only-input"
                                          placeholder="Gram"
                                          disabled={isUpdating}
                                          onWheel={(e) => e.preventDefault()}
                                        />
                                      ) : (
                                        <input
                                          type="text"
                                          value=""
                                          className="read-only-input"
                                          disabled
                                          style={{ backgroundColor: "#f0f0f0" }}
                                          placeholder="N/A"
                                        />
                                      )}
                                    </td>
                                  </>
                                );
                              })()}
                              {/* Box column */}
                              {(() => {
                                const updateCategories = updateInvoice.products.map((p) =>
                                  (p.category || "").toLowerCase()
                                );
                                if (!updateCategories.includes("box")) return null;
                                return (
                                  <td>
                                    {isBox ? (
                                      <input
                                        type="text"
                                        value={p.boxSize ?? ""}
                                        onChange={(e) =>
                                          handleUpdateProductChange(
                                            idx,
                                            "boxSize",
                                            e.target.value
                                          )
                                        }
                                        className="read-only-input"
                                        placeholder="Box Size"
                                        disabled={isUpdating}
                                      />
                                    ) : (
                                      <input
                                        type="text"
                                        value=""
                                        className="read-only-input"
                                        disabled
                                        style={{ backgroundColor: "#f0f0f0" }}
                                        placeholder="N/A"
                                      />
                                    )}
                                  </td>
                                );
                              })()}
                              {/* Vendor/Particular */}
                              <td>
                                {cat !== "roll" ? (
                                  isReelOrTota ? (
                                    <>
                                      <input
                                        type="text"
                                        list={`update-vendors-${idx}`}
                                        value={p.particular ?? ""}
                                        onChange={(e) =>
                                          handleUpdateProductChange(
                                            idx,
                                            "particular",
                                            e.target.value
                                          )
                                        }
                                        className="read-only-input"
                                        placeholder="Select Vendor"
                                        disabled={isUpdating}
                                      />
                                      <datalist id={`update-vendors-${idx}`}>
                                        {vendorList.map((v) => (
                                          <option key={v.id} value={v.vendorName} />
                                        ))}
                                      </datalist>
                                    </>
                                  ) : (
                                    <input
                                      type="text"
                                      value={p.particular ?? ""}
                                      onChange={(e) =>
                                        handleUpdateProductChange(
                                          idx,
                                          "particular",
                                          e.target.value
                                        )
                                      }
                                      className="read-only-input"
                                      placeholder="Particular"
                                      disabled={isUpdating}
                                    />
                                  )
                                ) : (
                                  <input
                                    type="text"
                                    value=""
                                    className="read-only-input"
                                    disabled
                                    style={{ backgroundColor: "#f0f0f0" }}
                                    placeholder="Not applicable"
                                  />
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>

                <div style={{ marginTop: 12 }}>
                  <label style={{ fontWeight: "bold" }}>Description:</label>
                  <input
                    type="text"
                    value={updateInvoice.description ?? ""}
                    onChange={(e) => handleUpdateInvoiceField("description", e.target.value)}
                    className="read-only-input"
                    style={{ width: "60%" }}
                    disabled={isUpdating}
                  />
                </div>

                <div style={{ marginTop: 12, display: "flex", gap: 10 }}>
                  <button
                    className="save-btn"
                    onClick={saveUpdate}
                    disabled={isUpdating}
                    style={{
                      opacity: isUpdating ? 0.6 : 1,
                      cursor: isUpdating ? "not-allowed" : "pointer",
                    }}
                  >
                    {isUpdating ? "Saving..." : "Save Updated Invoice"}
                  </button>
                  <button
                    className="remove-btn"
                    onClick={() => setUpdateInvoice(null)}
                    disabled={isUpdating}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>
        );
      })}

      <div style={{ position: "absolute", top: "-9999px" }}>
        <UniversalInvoicePrint ref={printRef} invoice={printInvoice} type="sale" />
      </div>

      {fullScreenImage && (
        <div
          onClick={() => setFullScreenImage(null)}
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            backgroundColor: "rgba(0,0,0,0.8)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 9999,
            cursor: "zoom-out",
          }}
        >
          <img
            src={fullScreenImage}
            alt="Full Screen Invoice"
            style={{
              maxWidth: "90%",
              maxHeight: "90%",
              objectFit: "contain",
              boxShadow: "0 0 20px #fff",
            }}
          />
        </div>
      )}
    </div>
  );
}

export default ViewFactoryUse;